import {
  DOMConversionMap,
  EditorConfig,
  ElementNode,
  LexicalEditor,
} from 'lexical';
import { addClassNamesToElement } from '@lexical/utils';
import { createActionButton, deleteAction } from '../utils/actionButton.ts';

export class LayoutContainerNode extends ElementNode {
  __templateColumns: string;

  constructor(templateColumns: string, key?: string) {
    super(key);
    this.__templateColumns = templateColumns;
  }

  static getType() {
    return 'layout-container';
  }

  static clone(node: LayoutContainerNode) {
    return new LayoutContainerNode(node.__templateColumns, node.__key);
  }

  createDOM(config: EditorConfig, editor: LexicalEditor): HTMLElement {
    const dom = document.createElement('div');
    dom.className = 'PlaygroundEditorTheme__layoutContainer';
    dom.style.gridTemplateColumns = this.__templateColumns;
    dom.append(
      createActionButton(
        () => deleteAction(editor, () => this.remove()),
        'removeButton',
      ),
    );

    if (typeof config.theme.layoutContainer === 'string') {
      addClassNamesToElement(dom, config.theme.layoutContainer);
    }
    return dom;
  }

  updateDOM(prevNode: LayoutContainerNode, dom: HTMLElement): boolean {
    if (prevNode.__templateColumns !== this.__templateColumns) {
      dom.style.gridTemplateColumns = this.__templateColumns;
    }
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (domNode.hasAttribute('data-lexical-layout-container')) {
          return {
            conversion: () => {
              const template = domNode.style.gridTemplateColumns || '1fr';
              return {
                node: $createLayoutContainerNode(template),
              };
            },
            priority: 4,
          };
        }
        return null;
      },
    };
  }

  static importJSON(json: any) {
    return new LayoutContainerNode(json.templateColumns);
  }

  exportDOM() {
    const element = document.createElement('div');
    element.className = 'PlaygroundEditorTheme__layoutContainer';
    element.style.gridTemplateColumns = this.__templateColumns;
    element.setAttribute('data-lexical-layout-container', 'true');
    return { element };
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      templateColumns: this.__templateColumns,
      type: 'layout-container',
      version: 1,
    };
  }

  getTemplateColumns() {
    return this.__templateColumns;
  }

  setTemplateColumns(templateColumns: string) {
    this.__templateColumns = templateColumns;
  }
}

export function $createLayoutContainerNode(templateColumns: string) {
  return new LayoutContainerNode(templateColumns);
}

export function $isLayoutContainerNode(node: any): node is LayoutContainerNode {
  return node instanceof LayoutContainerNode;
}
